// src/components/LoginModal.js
import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase-config';
import { locationData } from '../utils/constants';
import './LoginModal.css';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path fill="#4285F4" d="M22.56,12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26,1.37-1.04,2.53-2.21,3.31v2.77h3.57c2.08-1.92,3.28-4.74,3.28-8.09Z"/>
    <path fill="#34A853" d="M12,23c2.97,0,5.46-.98,7.28-2.66l-3.57-2.77c-.98,.66-2.23,1.06-3.71,1.06-2.86,0-5.29-1.93-6.16-4.53H2.18v2.84C3.99,20.53,7.7,23,12,23Z"/>
    <path fill="#FBBC05" d="M5.84,14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43,.35-2.09V7.07H2.18C1.43,8.55,1,10.22,1,12s.43,3.45,1.18,4.93l3.66-2.84Z"/>
    <path fill="#EA4335" d="M12,5.38c1.62,0,3.06,.56,4.21,1.64l3.15-3.15C17.46,1.31,14.97,0,12,0,7.7,0,3.99,2.47,2.18,5.93l3.66,2.84c.87-2.6,3.3-4.53,6.16-4.53Z"/>
  </svg>
);

const LoginModal = ({ onClose }) => {
  const { signup, login, loginWithGoogle } = useAuth();

  const [view, setView] = useState('login'); // 'login' | 'signup' | 'forgotPassword'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  // --- NEW FIELDS ---
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [dob, setDob] = useState('');
  // ------------------
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [selectedMandal, setSelectedMandal] = useState('');

  // 1. Memoize Districts based on selected state
  const availableDistricts = useMemo(() => {
    if (!selectedState) return [];
    const stateObj = locationData.find(s => s.state_en === selectedState);
    return stateObj ? stateObj.districts : [];
  }, [selectedState]);

  // Find the selected district object so we can access its constituencies and mandals
  const selectedDistrictObj = useMemo(() => {
    if (!selectedDistrict) return null;
    return availableDistricts.find(d => d.district_en === selectedDistrict) || null;
  }, [selectedDistrict, availableDistricts]);

  // 2. Memoize Constituencies based on selected district
  const availableConstituencies = useMemo(() => {
    return selectedDistrictObj ? selectedDistrictObj.constituencies || [] : [];
  }, [selectedDistrictObj]);

  // 3. Memoize Mandals based on selected district
  const availableMandals = useMemo(() => {
    return selectedDistrictObj ? selectedDistrictObj.mandals || [] : [];
  }, [selectedDistrictObj]);

  const resetForm = () => {
    setName(''); setEmail(''); setPassword('');
    setPhoneNumber(''); setGender(''); setAge(''); setDob('');
    setSelectedState(''); setSelectedDistrict('');
    setSelectedConstituency(''); setSelectedMandal('');
    setError(''); setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // --- Age/DOB validation on signup ---
    if (view === 'signup') {
      if (!selectedState || !selectedDistrict || !selectedConstituency || !selectedMandal) {
        return setError('Please complete all location selections (State, District, Constituency, Mandal).');
      }
      if (!gender) return setError('Please select your gender.');
      if (!age || isNaN(age) || age < 1 || age > 120) return setError('Please enter a valid age.');
      if (!dob) return setError('Please enter your date of birth.');
    }

    setLoading(true);
    try {
      if (view === 'signup') {
        await signup(email, password, name, phoneNumber, gender, parseInt(age), dob, {
        state: selectedState,
        district: selectedDistrict,
        constituency: selectedConstituency,
        mandal: selectedMandal
    });
        setSuccess('Signup successful! You can now log in.');
        resetForm();
        setView('login');
      } else {
        await login(email, password);
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Password reset link sent! Check your inbox and spam folder.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await loginWithGoogle();
      // On mobile, loginWithGoogle() triggers a redirect and result is undefined.
      // The page navigates away — onClose() never needs to be called.
      // On desktop, result is returned from the popup.
      if (result) {
        onClose();
      }
      // On mobile: loading spinner stays visible briefly while redirect happens
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Login cancelled. Please try again.');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setError('A login popup is already open.');
      } else {
        setError('Google Login failed: ' + err.message);
      }
      setLoading(false); // Only reset loading on error; redirect handles the rest
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content login-modal">
        <div className="modal-header">
          <h2>
            {view === 'login' && 'Welcome Back'}
            {view === 'signup' && 'Create Your Account'}
            {view === 'forgotPassword' && 'Reset Your Password'}
          </h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <p className="error-message">{error}</p>}
          {success && <p className="success-message">{success}</p>}

          {view === 'login' && (
    <div className="login-info-banner">
        <i className="fas fa-info-circle"></i>
        <div>
            <p className="telugu-text">
                మీరు కొత్త యూజర్ అయితే, మొదట <strong>సైన్ అప్ (Sign Up)</strong> అవ్వండి.
            </p>
            <p className="telugu-subtext">
                వార్తలు పోస్ట్ చేసి డబ్బు సంపాదించoడి..
            </p>
            <small className="english-hint">(New users please Sign Up first to post news and earn money)</small>
        </div>
        <button onClick={() => setView('signup')} className="mini-signup-link">
          Sign Up ఇక్కడ చేయండి
        </button>
    </div>
  )}

          {view === 'forgotPassword' ? (
            <form onSubmit={handlePasswordReset}>
              <p className="forgot-password-info">
                Enter your email address and we'll send you a reset link.
              </p>
              <div className="form-group">
                <label htmlFor="reset-email">Email Address</label>
                <input
                  type="email" id="reset-email"
                  value={email} onChange={(e) => setEmail(e.target.value)} required
                />
              </div>
              <button type="submit" className="button-primary" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email" id="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} required
                />
              </div>

              {view === 'signup' && (
                <>
                  {/* Full Name */}
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      type="text" id="name"
                      value={name} onChange={(e) => setName(e.target.value)} required
                    />
                  </div>

                  {/* Phone */}
                  <div className="form-group">
                    <label htmlFor="phone">10-Digit Phone Number</label>
                    <input
                      type="tel" id="phone"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      pattern="[0-9]{10}"
                      title="Enter a valid 10-digit phone number"
                      required
                    />
                  </div>

                  {/* Gender */}
                  <div className="form-group">
                    <label htmlFor="gender">Gender</label>
                    <select
                      id="gender"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      required
                    >
                      <option value="">-- Select Gender --</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="non-binary">Non-binary</option>
                      <option value="prefer-not-to-say">Prefer not to say</option>
                    </select>
                  </div>

                  {/* Age */}
                  <div className="form-group">
                    <label htmlFor="age">Age</label>
                    <input
                      type="number" id="age"
                      min="1" max="120"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      required
                    />
                  </div>

                  {/* Date of Birth */}
                  <div className="form-group">
                    <label htmlFor="dob">Date of Birth</label>
                    <input
                      type="date" id="dob"
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} // no future dates
                      required
                    />
                  </div>
                  {/* 4. STATE DROPDOWN */}
                  <div className="form-group">
                    <label>రాష్ట్రం (State)</label>
                    <select 
                      value={selectedState} 
                      onChange={(e) => {
                        setSelectedState(e.target.value);
                        setSelectedDistrict(''); // Reset district when state changes
                        setSelectedConstituency('');
                        setSelectedMandal('');
                        }} 
                      required
                    >
            <option value="">-- Select State --</option>
            {locationData.map(s => (
              <option key={s.state_en} value={s.state_en}>
                {s.state_te} ({s.state_en})
              </option>
            ))}
          </select>
        </div>

        {/* 5. DISTRICT DROPDOWN (Conditional) */}
        {selectedState && (
          <div className="form-group">
            <label>జిల్లా (District)</label>
            <select 
              value={selectedDistrict} 
              onChange={(e) => {
                setSelectedDistrict(e.target.value);
                setSelectedConstituency(''); // Reset options below district
                setSelectedMandal('');
              }} 
              required
            >
              <option value="">-- Select District --</option>
              {availableDistricts.map(d => (
                <option key={d.district_en} value={d.district_en}>
                  {d.district_te || d.district_en}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* 6. CONSTITUENCY DROPDOWN */}
        {selectedDistrict && (
          <div className="form-group">
            <label>నియోజకవర్గం (Constituency)</label>
            <select 
              value={selectedConstituency} 
              onChange={(e) => setSelectedConstituency(e.target.value)} 
              required
            >
              <option value="">-- Select Constituency --</option>
              {availableConstituencies.map(c => (
                <option key={c.value} value={c.value}>
                  {c.label} ({c.value})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 7. MANDAL DROPDOWN */}
        {selectedDistrict && (
          <div className="form-group">
            <label>మండలం (Mandal)</label>
            <select 
              value={selectedMandal} 
              onChange={(e) => setSelectedMandal(e.target.value)} 
              required
            >
              <option value="">-- Select Mandal --</option>
              {availableMandals.map(m => (
                <option key={m.value} value={m.value}>
                  {m.label} ({m.value})
                </option>
              ))}
            </select>
          </div>
        )}
                </>
              )}

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password" id="password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required
                />
              </div>

              {view === 'login' && (
                <div
                  className="forgot-password-link"
                  onClick={() => { setError(''); setSuccess(''); setView('forgotPassword'); }}
                >
                  Forgot Password?
                </div>
              )}

              <button type="submit" className="button-primary" disabled={loading}>
                {loading ? 'Processing...' : (view === 'signup' ? 'Sign Up' : 'Log In')}
              </button>
            </form>
          )}

          {view === 'login' && (
            <>
              <div className="divider">OR</div>
              <button onClick={handleGoogleLogin} className="google-btn" disabled={loading}>
                <GoogleIcon />
                <span>Sign in with Google</span>
              </button>
            </>
          )}
        </div>

        <div className="modal-footer text-center">
          {view !== 'forgotPassword' && (
            <p>
              {view === 'signup' ? 'Already have an account? ' : "Don't have an account? "}
              <span
                className="toggle-auth-mode"
                onClick={() => { resetForm(); setView(view === 'login' ? 'signup' : 'login'); }}
              >
                {view === 'login' ? 'Sign Up Now' : 'Log In Here'}
              </span>
            </p>
          )}
          {view === 'forgotPassword' && (
            <p>
              <span className="toggle-auth-mode" onClick={() => { setError(''); setSuccess(''); setView('login'); }}>
                Back to Login
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;